import { Box, FormControl, FormLabel, FormErrorMessage, Text, VStack, HStack, Spinner, useColorModeValue } from "@chakra-ui/react";
import { useState, useEffect, useRef, useLayoutEffect, forwardRef, useImperativeHandle } from "react";

import { $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Nullable } from "foundation-ts/types";

import { config } from "@/config/config";
import { FieldKey, MollieComponent, MollieComponentEvent, MollieInstance, PaymentFormRef } from "./types";

interface MollieCardProps {
    signup?: boolean;
    initializeMollie: () => MollieInstance;
    onSubmit: (token: string, captchaToken: string) => Promise<void>;
    onValidityChange: (isValid: boolean) => void;
}

export const MollieCard = forwardRef<PaymentFormRef, MollieCardProps>((props, ref) => {
    useImperativeHandle(ref, () => ({
        submit: async (captchaToken: string) => {
            const { token, error } = await mollie!.createToken();
            if ($ok(error) || !$ok(token)) { TSError.throw(error?.message ?? "Erreur d'initialisation du service mollie", -1); }

            await onSubmit(token!, captchaToken);
        }
    }));

    const { signup, initializeMollie, onSubmit, onValidityChange } = props;
    const [mollie, setMollie] = useState<Nullable<MollieInstance>>(null);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        cardHolder: null,
        cardNumber: null,
        expiryDate: null,
        verificationCode: null,
    });
    const [mollieReady, setMollieReady] = useState<boolean>(false);
    const mountedRef = useRef<boolean>(false);
    const componentsRef = useRef<Partial<Record<FieldKey, MollieComponent>>>({});
    const eventListenersRef = useRef<Array<() => void>>([]);
    const fieldValidsRef = useRef<Record<FieldKey, boolean>>({
        cardHolder: false,
        cardNumber: false,
        expiryDate: false,
        verificationCode: false,
    });

    const mandateBg = "orange.900"; // useColorModeValue("orange.50", "orange.900");
    const mandateText = "orange.100"; // useColorModeValue("orange.800", "orange.100");
    const mandateBorder = "orange.600"; // useColorModeValue("orange.300", "orange.600");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const mollieStyles = {
        base: {
            color: "#E2E8F0",
            fontSize: "14px",
            lineHeight: "38px",
            "::placeholder": { color: "#4A5568" },
        },
        valid:   { color:"#E2E8F0" },
        invalid: { color: "#E53E3E" },
    };

    function initMollie(): void {
        if (mountedRef.current) { return; }
        mountedRef.current = true;

        const m = initializeMollie(); //window.Mollie(profileId, { locale: "fr_FR", testmode: testMode });
        const cardHolder       = m.createComponent("cardHolder",       { styles: mollieStyles });
        const cardNumber       = m.createComponent("cardNumber",       { styles: mollieStyles });
        const expiryDate       = m.createComponent("expiryDate",       { styles: mollieStyles });
        const verificationCode = m.createComponent("verificationCode", { styles: mollieStyles });
 
        const mountMap: Array<{ component?: MollieComponent; selector: string; key: FieldKey }> = [
            { component: cardHolder,      selector: "#mollie-card-holder",        key: "cardHolder" },
            { component: cardNumber,      selector: "#mollie-card-number",        key: "cardNumber" },
            { component: expiryDate,      selector: "#mollie-expiry-date",        key: "expiryDate" },
            { component: verificationCode,selector: "#mollie-verification-code",  key: "verificationCode" },
        ];
 
        const mounted: Partial<Record<FieldKey, MollieComponent>> = {};
        mountMap.forEach(({ component, selector, key }) => {
            if (!$ok(document.querySelector(selector))) { return; }

            component?.mount(selector);
            mounted[key] = component;
            const listener = (event: MollieComponentEvent) => {
                fieldValidsRef.current[key] = !$ok(event.error) && event.valid;
                setFieldErrors((prev) => ({
                    ...prev,
                    [key]: event.error,
                }));
                onValidityChange(Object.values(fieldValidsRef.current).every(Boolean));
            };
            component?.addEventListener("change", listener);
            eventListenersRef.current.push(() => {
                component?.removeEventListener("change", listener);
            });
        })

        componentsRef.current = mounted;
        setMollie(m);
        setMollieReady(true);
    }

    const mollieField = (id: string, errorKey: FieldKey, showSpinner: boolean = false) => (
        <>
            {showSpinner && !mollieReady && (
                <HStack h="40px" px={3} border="1px solid" borderColor={borderColor} borderRadius="md" bg="transparent">
                    <Spinner size="xs" color="gray.400" />
                    <Text fontSize="xs" color="gray.400">Chargement sécurisé…</Text>
                </HStack>
            )}
            <Box id={id} h="40px" px={3} border="1px solid" borderColor={fieldErrors[errorKey] ? "red.400" : borderColor} borderRadius="md" bg="transparent"
                 sx={mollieReady ? {
                    "&::selection, &:focus, &:active": {
                        backgroundColor: "transparent !important",
                        color: "inherit !important",
                    },
                } : { display: "none" }}
             />
        </>
    );

    useLayoutEffect(() => {
        return () => {
            mountedRef.current = false;
            Object.values(componentsRef.current).forEach((c) => {
                try {
                    c?.unmount();
                } catch {
                }
            });
            eventListenersRef.current.forEach((cleanup) => cleanup());
            eventListenersRef.current = [];
            componentsRef.current = {};
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        let scriptRef: HTMLScriptElement | null = null;

        function safeInitMollie(): void {
            if (cancelled) { return; }
            initMollie();
            setMollieReady(true);
        }

        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="${config.app.mollie.scriptURL}"]`
        );
 
        if ($ok(existingScript)) {
            if (existingScript!.dataset.loaded === "true") {
                safeInitMollie();
            } else {
                existingScript!.addEventListener("load", safeInitMollie, { once: true });
            }
        } else {
            const script = document.createElement("script");
            script.src = config.app.mollie.scriptURL!;
            script.addEventListener("load", () => {
                script.dataset.loaded = "true";
                safeInitMollie();
            }, { once: true });
            document.head.appendChild(script);
            scriptRef = script;
        }

        return () => {
            cancelled = true;
            mountedRef.current = false;
            if (scriptRef && scriptRef.dataset.loaded !== "true") {
                scriptRef.removeEventListener("load", safeInitMollie);
            }
        };
    }, []);

    return (
        <VStack spacing={4}>
            <FormControl isRequired isInvalid={$ok(fieldErrors.cardHolder)}>
                <FormLabel fontSize="sm">Titulaire de la carte</FormLabel>
                { mollieField("mollie-card-holder", "cardHolder", true) }
                <Text fontSize="xs" color="gray.500" mt={1}>
                    Saisissez le nom tel qu'il apparaît sur la carte
                </Text>
                <FormErrorMessage color="red.200">{fieldErrors.cardHolder}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={$ok(fieldErrors.cardNumber)}>
                <FormLabel fontSize="sm">Numéro de carte</FormLabel>
                { mollieField("mollie-card-number", "cardNumber") }
                <FormErrorMessage color="red.200">{fieldErrors.cardNumber}</FormErrorMessage>
            </FormControl>

            <HStack w="full" spacing={4} align="flex-start">
                <FormControl isRequired isInvalid={$ok(fieldErrors.expiryDate)}>
                    <FormLabel fontSize="sm">Expiration</FormLabel>
                    { mollieField("mollie-expiry-date", "expiryDate") }
                    <FormErrorMessage color="red.200">{fieldErrors.expiryDate}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={$ok(fieldErrors.verificationCode)}>
                    <FormLabel fontSize="sm">CVV</FormLabel>
                    { mollieField("mollie-verification-code", "verificationCode") }
                    <FormErrorMessage color="red.200">{fieldErrors.verificationCode}</FormErrorMessage>
                </FormControl>
            </HStack>

            {!!signup ? (
                <Box mt={5} p={4} bg={mandateBg} border="1px solid" borderColor={mandateBorder} borderRadius="md">
                    <Text fontSize="xs" color={mandateText} lineHeight="1.7">
                        Un premier paiement d'un montant de 0€ est initié. En validant ce paiement, un mandat Carte Bancaire est créé 
                        et sera automatiquement utilisé pour les prochains paiements.
                    </Text>
                </Box>
                ) : ''
            }
        </VStack>
    );
});

type FieldErrors = Record<FieldKey, string | null>;